import React from "react"
import L10n from "lib/carrot-l10n"
import { Spacer } from "components/views/atoms/Atoms"
import { H2, Body, Container, FlexContainer } from "@carrotfertility/carotene"
import { useSelector } from "react-redux"
import { getShouldFillOutBankInfo } from "../../../../derivedSelectors"
import { useStateMachine } from "../../../context/stateMachine/StateMachineContext"
import { IlloCoinsAndBills } from "../../../views/atoms/Illustrations"
import { Steps } from "../../workflow/steps"
import { ButtonPrimary } from "../../../views/atoms/buttons/ButtonPrimary"
import { Button } from "../../../views/atoms/buttons/Button"
import { useHistory } from "react-router-dom"
import { TextLink } from "../../../views/atoms/links/TextLink"
import Settings from "../../../../utils/CarrotConfig"
import { useEnrollmentFlowModal } from "components/csd-enrollment/EnrollmentModal"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

export function FinalStep(): JSX.Element {
  const { onExit } = useEnrollmentFlowModal()
  useStateMachine(Steps.FINAL, null)
  const needsBankInfo = useSelector(getShouldFillOutBankInfo)

  return (
    <div>
      <Spacer height={2} />
      <FlexContainer justifyContent="center" padding="none" stack="large">
        <IlloCoinsAndBills />
      </FlexContainer>
      <H2 stack="small" variant="primary">{l`You're all set.`}</H2>
      <Container padding="none" stack="medium">
        {!needsBankInfo && <Body color="black-80">{l`We have all the information we need for now.`}</Body>}
      </Container>
      <Container padding="none" stack="medium">
        <Body color="black-80">
          {l`To check the status of any deductibles, see the `}
          <TextLink href="/transactions">{l`Transactions`}</TextLink>
          {l` tab. You can read more about deductibles in the `}
          <TextLink href={Settings.ZENDESK_GUIDE}>{l`Support Center`}</TextLink>.
        </Body>
      </Container>
      <BankInfoButtonBar {...{ onExit }} />
    </div>
  )
}

export function FinalNoDeductibleStep(): JSX.Element {
  const { onExit } = useEnrollmentFlowModal()
  useStateMachine(Steps.FINAL_NO_DEDUCTIBLE, null)

  return (
    <div>
      <Spacer height={2} />
      <FlexContainer justifyContent="center" padding="none" stack="large">
        <IlloCoinsAndBills />
      </FlexContainer>
      <H2 stack="small" variant="primary">{l`You're all set.`}</H2>
      <Container padding="none" stack="medium">
        <Body color="black-80">{l`We have all the information we need for now.`}</Body>
      </Container>
      <Container padding="none" stack="medium">
        <Body color="black-80">
          {l`It looks like you do not have a deductible associated with your Carrot benefit. That means we can start reimbursing you right away.`}
        </Body>
      </Container>
      <BankInfoButtonBar {...{ onExit }} />
    </div>
  )
}

interface BankInfoButtonBarProps {
  onExit: () => void
}

const BankInfoButtonBar = ({ onExit }: BankInfoButtonBarProps): JSX.Element => {
  const history = useHistory()
  const needsBankInfo = useSelector(getShouldFillOutBankInfo)
  const handleUpdateBankDetailsClick = () => {
    history.push("/account#bankdetails")
    onExit()
  }

  return needsBankInfo ? (
    <>
      <Container padding="none" stack="large">
        <Spacer height={0.5} />
        <Body color="black-80">{l`Next, update your bank info so we can direct those reimbursement funds to the right account.`}</Body>
        <Spacer height={0.5} />
      </Container>
      <FlexContainer padding="none" justifyContent="center">
        <ButtonPrimary className="mr3" onClick={handleUpdateBankDetailsClick} enabled={true}>
          Update bank info
        </ButtonPrimary>
        <Button outline onClick={onExit} enabled>
          {l`I'll do this later`}
        </Button>
      </FlexContainer>
    </>
  ) : (
    <FlexContainer justifyContent="center">
      <ButtonPrimary onClick={onExit} enabled={true}>
        Close
      </ButtonPrimary>
    </FlexContainer>
  )
}
